// src/pages/team/TeamListPage.tsx

import { useEffect, useState, useMemo, useCallback, type MouseEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n, useAuth } from '@react/hooks';
import { ListMyTeamInvitations } from '@core/api/endpoints/team/list_my_team_invitations';
import { ListTeams } from '@core/api/endpoints/team/list_teams';
import { ListTeamJoinApplications } from '@core/api/endpoints/team/list_team_join_applications';
import type { Team, TeamInvitation } from '@core/types/team';
import { Button, Input, Badge, Card, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { SegmentedControl } from '@/components/form';
import { PaneControls } from '@/features/tabs/PaneChrome';
import { AnchoredMenu, MenuItem, MenuSeparator } from '@/components/AnchoredMenu';
import { useTabId } from '@/components/tabs/TabContext';
import { useNarrowViewport } from '@/hooks/useNarrowViewport';
import { tabsStore } from '@/stores/TabsStore';
import { TeamCreateDialog } from '@/features/team/TeamCreateDialog';
import { JoinRequestDialog } from '@/features/team/JoinRequestDialog';
import { EntityAvatar } from '@/components/EntityAvatar';
import { usePageActions } from '@/hooks/usePageActions';
import { ACTION_ICONS } from '@/features/layout/actionIcons';
import { datedFilename, downloadCsv } from '@/features/export/csv';
import {
  teamFilters,
  teamFilterToType,
  teamStatusMap,
  teamTypeMap,
  teamVisibilityMap,
  invitationStatusMap,
  getStatusMapping,
  formatRelativeTime,
  getEntityId,
  type TeamFilter,
} from '@/features/team/logic';

type PageTab = 'mine' | 'discover' | 'invitations';
type TeamView = 'cards' | 'list' | 'tree';
type TeamCardData = Team & { members_count?: number; member_count?: number };

function isTeamView(value: string | null): value is TeamView {
  return value === 'cards' || value === 'list' || value === 'tree';
}

async function fetchTeamScope(scope?: 'direct_member' | 'direct_admin'): Promise<TeamCardData[]> {
  const items: TeamCardData[] = [];
  let cursor = '';
  const seenCursors = new Set<string>();
  let hasNext = true;
  while (hasNext) {
    const response = await ListTeams({
      membership_scope: scope,
      include_secret: true,
      view: 2,
      pagination: { cursor, limit: 100, direction: 'forward' },
    } as never);
    items.push(...(response.teams?.teams ?? []));
    const info = response.pagination?.page_info;
    hasNext = !!info?.has_next && !!info.next_cursor && !seenCursors.has(info.next_cursor);
    if (!hasNext || !info) break;
    seenCursors.add(info.next_cursor);
    cursor = info.next_cursor;
  }
  return items;
}

async function fetchPendingJoinCount(teamId: string): Promise<number> {
  let count = 0;
  let cursor = '';
  const seen = new Set<string>();
  let hasNext = true;
  while (hasNext) {
    const result = await ListTeamJoinApplications(teamId, { pagination: { cursor, limit: 100, direction: 'forward' } });
    count += (result.applications?.applications ?? []).filter((app) => app.status === 'APPLICATION_STATUS_PENDING').length;
    const info = result.pagination?.page_info;
    hasNext = !!info?.has_next && !!info.next_cursor && !seen.has(info.next_cursor);
    if (!hasNext || !info) break;
    seen.add(info.next_cursor);
    cursor = info.next_cursor;
  }
  return count;
}

async function fetchMyInvitations(): Promise<(TeamInvitation & { team_name?: string })[]> {
  const invitations: (TeamInvitation & { team_name?: string })[] = [];
  let cursor = '';
  const seen = new Set<string>();
  let hasNext = true;
  while (hasNext) {
    const result = await ListMyTeamInvitations({ pagination: { cursor, limit: 100, direction: 'forward' } });
    invitations.push(...(result.invitations?.invitations ?? []));
    const info = result.pagination?.page_info;
    hasNext = !!info?.has_next && !!info.next_cursor && !seen.has(info.next_cursor);
    if (!hasNext || !info) break;
    seen.add(info.next_cursor);
    cursor = info.next_cursor;
  }
  return invitations;
}

export const TeamListPage = observer(() => {
  const store = useTeam();
  const { t, locale } = useI18n();
  const { activeProfileId } = useAuth();
  const navigate = useNavigate();
  const tabId = useTabId();
  const narrow = useNarrowViewport();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);

  const [teams, setTeams] = useState<TeamCardData[]>([]);
  const [catalogTeams, setCatalogTeams] = useState<TeamCardData[]>([]);
  const [myTeams, setMyTeams] = useState<TeamCardData[]>([]);
  const [adminTeamIds, setAdminTeamIds] = useState<Set<string>>(new Set());
  const [pendingApplications, setPendingApplications] = useState<Map<string, number>>(new Map());
  const [loadingMine, setLoadingMine] = useState(true);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [typeFilter, setTypeFilter] = useState<TeamFilter>((searchParams.get('type') as TeamFilter) || 'all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? 'active');
  const [roleFilter, setRoleFilter] = useState(searchParams.get('role') ?? 'all');
  const [view, setView] = useState<TeamView>('cards');
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [joinTarget, setJoinTarget] = useState<TeamCardData | null>(null);
  const [requestedIds, setRequestedIds] = useState<Set<string>>(new Set());
  const [contextMenu, setContextMenu] = useState<{ team: TeamCardData; x: number; y: number } | null>(null);
  const contextAnchor = useMemo(
    () => ({ x: contextMenu?.x ?? 0, y: contextMenu?.y ?? 0 }),
    [contextMenu],
  );

  const showTeamMenu = (team: TeamCardData, x: number, y: number) => {
    setContextMenu({ team, x, y });
  };

  const openTeamFromMenu = (mode: 'current' | 'tab' | 'left' | 'right' | 'up' | 'down') => {
    if (!contextMenu) return;
    const team = contextMenu.team;
    const path = `/teams/${getEntityId(team)}`;
    const title = team.name || t('common.untitled');
    markRecent(getEntityId(team));
    setContextMenu(null);
    if (mode === 'current' || (narrow && mode !== 'tab')) {
      navigate(path);
      return;
    }
    const group = tabsStore.groups.find((candidate) => candidate.tabIds.includes(tabId)) ?? tabsStore.focusedGroup;
    if (!group) {
      navigate(path);
      return;
    }
    const sourceTabId = group.activeTabId;
    tabsStore.openTab(path, title, { groupId: group.id });
    if (mode === 'tab') return;
    const openedTabId = tabsStore.groups.find((candidate) => candidate.id === group.id)?.activeTabId;
    if (!openedTabId || openedTabId === tabId) return;
    if (sourceTabId && sourceTabId !== openedTabId) tabsStore.activateTab(sourceTabId);
    tabsStore.splitWithTab(group.id, openedTabId, mode === 'left' || mode === 'right' ? 'row' : 'column', mode === 'left' || mode === 'up');
  };

  // Page-level tab
  const pageTab: PageTab = searchParams.get('tab') === 'discover' ? 'discover'
    : searchParams.get('tab') === 'invitations' ? 'invitations' : 'mine';
  const displayView = pageTab === 'discover' && view === 'tree' ? 'cards' : view;
  const isLoading = pageTab === 'discover' ? loadingMine || loadingCatalog : loadingMine;
  const setPageTab = (tab: PageTab) => {
    setSearch('');
    setTypeFilter('all');
    setStatusFilter('active');
    setRoleFilter('all');
    setSearchParams(tab === 'mine' ? {} : { tab });
  };

  const updateFilter = (key: string, value: string, defaultValue: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === defaultValue || value === '') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    setSearch(searchParams.get('q') ?? '');
    setTypeFilter((searchParams.get('type') as TeamFilter) || 'all');
    setStatusFilter(searchParams.get('status') ?? 'active');
    setRoleFilter(searchParams.get('role') ?? 'all');
    const urlView = searchParams.get('view');
    if (isTeamView(urlView)) {
      setView(urlView);
    } else {
      const storedView = localStorage.getItem('teams:view:' + (activeProfileId || 'default'));
      setView(isTeamView(storedView) ? storedView : 'cards');
    }
  }, [searchParams, activeProfileId]);

  const selectView = (nextView: TeamView) => {
    localStorage.setItem('teams:view:' + (activeProfileId || 'default'), nextView);
    setView(nextView);
    updateFilter('view', nextView, 'cards');
  };

  // Invitations state
  const [invitations, setInvitations] = useState<(TeamInvitation & { team_name?: string })[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationsError, setInvitationsError] = useState<string | null>(null);

  const loadMine = useCallback(() => {
    setLoadingMine(true);
    setError(null);
    return Promise.all([fetchTeamScope('direct_member'), fetchTeamScope('direct_admin')])
      .then(([members, admins]) => {
        setMyTeams(members);
        setAdminTeamIds(new Set(admins.map(getEntityId)));
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t('team.loadError')))
      .finally(() => setLoadingMine(false));
  }, [t]);

  const loadCatalog = useCallback(() => {
    setLoadingCatalog(true);
    setError(null);
    return fetchTeamScope()
      .then(setCatalogTeams)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t('team.loadError')))
      .finally(() => setLoadingCatalog(false));
  }, [t]);

  useEffect(() => { void loadMine(); }, [loadMine]);
  useEffect(() => { if (pageTab === 'discover') void loadCatalog(); }, [pageTab, loadCatalog]);
  useEffect(() => {
    if (adminTeamIds.size === 0) { setPendingApplications(new Map()); return; }
    let alive = true;
    Promise.allSettled([...adminTeamIds].map(async (teamId) => [teamId, await fetchPendingJoinCount(teamId)] as const)).then((results) => {
      if (alive) setPendingApplications(new Map(results.flatMap((result) => result.status === 'fulfilled' ? [result.value] : [])));
    });
    return () => { alive = false; };
  }, [adminTeamIds]);
  useEffect(() => {
    const mineIds = new Set(myTeams.map(getEntityId));
    setTeams(pageTab === 'discover' ? catalogTeams.filter((team) => !mineIds.has(getEntityId(team))) : myTeams);
  }, [pageTab, myTeams, catalogTeams]);

  // The count is visible before opening the invitations section.
  const loadInvitations = useCallback(() => {
    setInvitationsLoading(true);
    setInvitationsError(null);
    fetchMyInvitations()
      .then(setInvitations)
      .catch((err: unknown) => setInvitationsError(err instanceof Error ? err.message : t('team.loadError')))
      .finally(() => setInvitationsLoading(false));
  }, [t]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const pendingInvitations = useMemo(
    () => invitations.filter((inv) => inv.status === 'INVITATION_STATUS_PENDING'),
    [invitations],
  );
  const pendingApplicationCount = [...pendingApplications.values()].reduce((sum, count) => sum + count, 0);

  const handleAccept = async (invId: string) => {
    try {
      await store.AcceptTeamInvitation(invId, {});
      loadInvitations();
      void loadMine();
    } catch {
      // error handled by store
    }
  };

  const handleDecline = async (invId: string) => {
    try {
      await store.DeclineTeamInvitation(invId, {});
      loadInvitations();
    } catch {
      // error handled by store
    }
  };

  const pinKey = 'teams:pinned:' + (activeProfileId || 'default');
  const recentKey = 'teams:recent:' + (activeProfileId || 'default');
  useEffect(() => {
    try { setPinnedIds(JSON.parse(localStorage.getItem(pinKey) || '[]')); }
    catch { setPinnedIds([]); }
    try { setRecentIds(JSON.parse(localStorage.getItem(recentKey) || '[]')); }
    catch { setRecentIds([]); }
  }, [pinKey, recentKey]);

  const markRecent = (id: string) => {
    const next = [id, ...recentIds.filter((value) => value !== id)].slice(0, 20);
    setRecentIds(next);
    localStorage.setItem(recentKey, JSON.stringify(next));
  };

  const togglePin = (id: string) => {
    const next = pinnedIds.includes(id) ? pinnedIds.filter((value) => value !== id) : [...pinnedIds, id];
    setPinnedIds(next);
    localStorage.setItem(pinKey, JSON.stringify(next));
    setContextMenu(null);
  };

  const filtered = useMemo(() => teams.filter((team) => {
    const query = search.trim().toLocaleLowerCase();
    return (!query || (team.name + ' ' + team.description).toLocaleLowerCase().includes(query))
      && (typeFilter === 'all' || team.team_type === teamFilterToType[typeFilter])
      && (statusFilter === 'all' || team.status?.toLowerCase().endsWith(statusFilter))
      && (roleFilter !== 'admin' || adminTeamIds.has(getEntityId(team)));
  }).sort((a, b) => {
    const left = pinnedIds.indexOf(getEntityId(a));
    const right = pinnedIds.indexOf(getEntityId(b));
    if (left >= 0 || right >= 0) return left < 0 ? 1 : right < 0 ? -1 : left - right;
    const leftRecent = recentIds.indexOf(getEntityId(a));
    const rightRecent = recentIds.indexOf(getEntityId(b));
    if (leftRecent >= 0 || rightRecent >= 0) return leftRecent < 0 ? 1 : rightRecent < 0 ? -1 : leftRecent - rightRecent;
    return a.name.localeCompare(b.name);
  }), [teams, search, typeFilter, statusFilter, roleFilter, adminTeamIds, pinnedIds, recentIds]);

  const visibleIds = new Set(filtered.map(getEntityId));
  const children = new Map<string, TeamCardData[]>();
  if (pageTab === 'mine' && view === 'tree') {
    for (const team of filtered) {
      const parentId = team.parent?.id;
      const key = parentId && visibleIds.has(parentId) && parentId !== getEntityId(team) ? parentId : '';
      children.set(key, [...(children.get(key) ?? []), team]);
    }
  }
  const treeRows: { team: TeamCardData; depth: number; childCount: number }[] = [];
  const visited = new Set<string>();
  const visit = (key: string, depth: number) => {
    for (const team of children.get(key) ?? []) {
      const id = getEntityId(team);
      if (visited.has(id)) continue;
      visited.add(id);
      treeRows.push({ team, depth: Math.min(depth, 3), childCount: children.get(id)?.length ?? 0 });
      if (!collapsedIds.has(id)) visit(id, depth + 1);
    }
  };
  if (view === 'tree' && pageTab === 'mine') {
    visit('', 0);
    for (const team of filtered) if (!visited.has(getEntityId(team))) treeRows.push({ team, depth: 0, childCount: 0 });
  } else {
    for (const team of filtered) treeRows.push({ team, depth: 0, childCount: 0 });
  }
  const names = new Map(teams.map((team) => [getEntityId(team), team.name]));

  // ── Page actions (Right Rail) ────────────────────────────────────────
  // Occasional operations only — creating a team stays a button in the header.
  usePageActions([
    {
      key: 'export-csv',
      icon: ACTION_ICONS.export,
      label: t('workspace.actions.exportCsv', { count: filtered.length }),
      onSelect: () => downloadCsv(datedFilename('teams'), [
        [
          t('workspace.export.columns.name'),
          t('workspace.export.columns.type'),
          t('workspace.export.columns.visibility'),
          t('workspace.export.columns.members'),
        ],
        ...filtered.map((team) => [
          team?.name ?? '',
          team?.team_type ?? '',
          team?.visibility ?? '',
          team?.members_count ?? team?.member_count ?? 0,
        ]),
      ]),
    },
    {
      key: 'invitations',
      icon: ACTION_ICONS.envelope,
      label: t('workspace.actions.invitations', { count: pendingInvitations.length }),
      onSelect: () => setPageTab(pageTab === 'invitations' ? 'mine' : 'invitations'),
    },
    {
      key: 'refresh',
      icon: ACTION_ICONS.refresh,
      label: t('workspace.actions.refresh'),
      onSelect: () => { void (pageTab === 'discover' ? loadCatalog() : loadMine()); void loadInvitations(); },
    },
  ]);

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-lg font-semibold text-foreground">{t('team.list')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('team.directory.description')}
        </p>
      </div>

      <PaneControls>
        <Button size="sm" onClick={() => setShowCreate(true)}>{t('team.create')}</Button>
      </PaneControls>

      <nav className="flex gap-1 overflow-x-auto border-b border-border" aria-label={t('team.sectionsLabel')}>
        {(['mine', 'discover', 'invitations'] as PageTab[]).map((tab) => (
          <button key={tab} type="button" onClick={() => setPageTab(tab)}
            aria-current={pageTab === tab ? 'page' : undefined}
            className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${pageTab === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t('team.directory.' + tab)}
            {tab === 'invitations' && pendingInvitations.length + pendingApplicationCount > 0 && <span className="ml-2 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs text-primary">{pendingInvitations.length + pendingApplicationCount}</span>}
          </button>
        ))}
      </nav>

      {pageTab === 'mine' && !isLoading && (pendingInvitations.length > 0 || pendingApplicationCount > 0) && (
        <section className="rounded-lg border border-border bg-card px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">{t('team.directory.attention')}</h2>
          <div className="mt-2 flex flex-wrap gap-2">
            {pendingInvitations.length > 0 && <Button size="sm" variant="secondary" onClick={() => setPageTab('invitations')}>{t('team.directory.invitationsCount', { count: pendingInvitations.length })}</Button>}
            {pendingApplicationCount > 0 && <Button size="sm" variant="ghost" onClick={() => setPageTab('invitations')}>{t('team.directory.requestsCount', { count: pendingApplicationCount })}</Button>}
          </div>
        </section>
      )}

      {pageTab !== 'invitations' && (
        <>
          <div className="team-directory-filters" data-filter-count={pageTab === 'mine' ? '3' : '2'}>
            <Input
              aria-label={t('team.searchPlaceholder')}
              placeholder={t('team.searchPlaceholder')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); updateFilter('q', e.target.value, ''); }}
              className="!mb-0 min-w-0 [&_input]:h-10"
            />
            <Select fullWidth={false} className="min-w-0 [&>button]:h-10 [&>button>span]:truncate" aria-label={t('team.card.type')} value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value as TeamFilter); updateFilter('type', e.target.value, 'all'); }}>
              {teamFilters.map((filter) => <option key={filter.key} value={filter.key}>{t(filter.labelKey)}</option>)}
            </Select>
            <Select fullWidth={false} className="min-w-0 [&>button]:h-10 [&>button>span]:truncate" aria-label={t('team.directory.status')} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); updateFilter('status', e.target.value, 'active'); }}>
              {['active', 'inactive', 'archived', 'all'].map((value) => <option key={value} value={value}>{t('team.directory.status_' + value)}</option>)}
            </Select>
            {pageTab === 'mine' && <Select fullWidth={false} className="min-w-0 [&>button]:h-10 [&>button>span]:truncate" aria-label={t('team.directory.role')} value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); updateFilter('role', e.target.value, 'all'); }}>
              <option value="all">{t('team.directory.role_all')}</option><option value="admin">{t('team.directory.role_admin')}</option>
            </Select>}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {!isLoading && <span>{t('team.card.shown', { count: filtered.length })}</span>}
              {(search || typeFilter !== 'all' || statusFilter !== 'active' || roleFilter !== 'all') && <button type="button" className="text-primary hover:underline"
              onClick={() => { setSearch(''); setTypeFilter('all'); setStatusFilter('active'); setRoleFilter('all');
                const next = new URLSearchParams(searchParams); ['q', 'type', 'status', 'role'].forEach((key) => next.delete(key)); setSearchParams(next, { replace: true }); }}>{t('team.directory.clearFilters')}</button>}
            </div>
            <SegmentedControl size="sm" value={displayView} onChange={(value) => selectView(value as TeamView)}
              aria-label={t('team.directory.view')}
              options={(pageTab === 'mine' ? ['cards', 'list', 'tree'] : ['cards', 'list']).map((value) => ({ value, label: t('team.directory.view_' + value) }))} />
          </div>

          {/* Error */}
          {(error || store.error) && (
            <ErrorAlert error={store.error || error} dismissible onDismiss={() => { setError(null); store.clearError(); }} />
          )}

          {/* Loading */}
          {isLoading ? (
            <div className="space-y-2" aria-label={t('team.loading')}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : error ? null : filtered.length === 0 ? (
            /* Empty state */
            <div className="py-16 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                </svg>
              </div>
              <p className="text-lg font-medium text-foreground">
                {search || typeFilter !== 'all' ? t('team.noSearchResults') : pageTab === 'mine' ? t('team.directory.emptyMine') : catalogTeams.length > 0 && teams.length === 0 ? t('team.directory.allJoined') : t('team.directory.emptyDiscover')}
              </p>
              {/* What the page is for, said where it actually helps. */}
              <p className="mt-1 text-sm text-muted-foreground">
                {search ? t('team.tryDifferentSearch') : pageTab === 'mine' ? t('team.directory.emptyMineHint') : catalogTeams.length > 0 && teams.length === 0 ? t('team.directory.allJoinedHint') : t('team.directory.emptyDiscoverHint')}
              </p>
              {!search && pageTab === 'mine' && (
                <Button size="sm" variant="secondary" className="mt-4" onClick={() => setPageTab('discover')}>{t('team.directory.discover')}</Button>
              )}
            </div>
          ) : (
            <>
              {displayView === 'cards' ? <ul className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 17rem), 1fr))' }}>
                {filtered.map((team) => (
                  <TeamGridCard key={getEntityId(team)} team={team}
                    parentName={names.get(team.parent?.id ?? '')}
                    role={pageTab === 'discover'
                      ? t(teamVisibilityMap[team.visibility ?? '']?.labelKey ?? 'team.directory.discover')
                      : adminTeamIds.has(getEntityId(team)) ? t('team.directory.role_admin') : t('team.directory.role_member')}
                    pinned={pinnedIds.includes(getEntityId(team))}
                    requested={requestedIds.has(getEntityId(team))}
                    onOpen={() => markRecent(getEntityId(team))}
                    onJoin={pageTab === 'discover' && team.status === 'TEAM_STATUS_ACTIVE' && team.visibility !== 'TEAM_VISIBILITY_SECRET' ? () => setJoinTarget(team) : undefined}
                    onContextMenu={(e) => { e.preventDefault(); showTeamMenu(team, e.clientX, e.clientY); }}
                    onMenuClick={(rect) => showTeamMenu(team, rect.right, rect.bottom)} />
                ))}
              </ul> : <ul className="divide-y divide-border rounded-lg border border-border bg-card">
                {treeRows.map(({ team, depth, childCount }) => (
                  <TeamCard
                    key={getEntityId(team)}
                    team={team}
                    depth={depth}
                    childCount={childCount}
                    collapsed={collapsedIds.has(getEntityId(team))}
                    onToggle={() => setCollapsedIds((current) => { const next = new Set(current); const id = getEntityId(team); if (next.has(id)) next.delete(id); else next.add(id); return next; })}
                    parentName={names.get(team.parent?.id ?? '')}
                    role={pageTab === 'discover'
                      ? t(teamVisibilityMap[team.visibility ?? '']?.labelKey ?? 'team.directory.discover')
                      : adminTeamIds.has(getEntityId(team)) ? t('team.directory.role_admin') : t('team.directory.role_member')}
                    pinned={pinnedIds.includes(getEntityId(team))}
                    requested={requestedIds.has(getEntityId(team))}
                    onOpen={() => markRecent(getEntityId(team))}
                    onJoin={pageTab === 'discover' && team.status === 'TEAM_STATUS_ACTIVE' && team.visibility !== 'TEAM_VISIBILITY_SECRET' ? () => setJoinTarget(team) : undefined}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      showTeamMenu(team, e.clientX, e.clientY);
                    }}
                    onMenuClick={(rect) => showTeamMenu(team, rect.right, rect.bottom)}
                  />
                ))}
              </ul>}
            </>
          )}
        </>
      )}

      {pageTab === 'invitations' && (
        <>
          <h2 className="text-sm font-semibold text-foreground">{t('team.directory.invitations')}</h2>
          {/* Error */}
          {(invitationsError || store.error) && (
            <ErrorAlert error={store.error || invitationsError} dismissible onDismiss={() => { setInvitationsError(null); store.clearError(); }} />
          )}

          {/* Loading */}
          {invitationsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 rounded-lg border border-border bg-card p-4 animate-pulse">
                  <div className="w-10 h-10 rounded-lg bg-muted flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/3 rounded bg-muted" />
                    <div className="h-3 w-1/2 rounded bg-muted" />
                  </div>
                  <div className="h-8 w-20 rounded bg-muted" />
                </div>
              ))}
            </div>
          ) : pendingInvitations.length === 0 ? (
            /* Empty state */
            <div className="rounded-lg border border-dashed border-border px-4 py-5">
              <p className="text-sm font-medium text-foreground">{t('team.noMyInvitations')}</p>
            </div>
          ) : (
            /* Invitations list */
            <div className="space-y-3">
              {pendingInvitations.map(inv => {
                const invId = getEntityId(inv);
                const isPending = inv.status === 'INVITATION_STATUS_PENDING';
                const statusInfo = getStatusMapping(invitationStatusMap, inv.status || 'INVITATION_STATUS_PENDING');
                return (
                  <div key={invId} className="flex items-center gap-4 rounded-lg border border-border bg-card p-4 hover:shadow-sm transition-all">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">{inv.team_name || t('team.title')}</span>
                        <Badge variant={statusInfo.variant as never} size="sm">{t(statusInfo.labelKey)}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 space-x-3">
                        {inv.inviter?.display_name && <span>{t('team.memberInvitedBy')}: {inv.inviter.display_name}</span>}
                        <span>{formatRelativeTime(inv.sent_at, locale)}</span>
                      </div>
                      {inv.message && <p className="text-xs text-muted-foreground mt-1 italic">"{inv.message}"</p>}
                    </div>
                    {isPending && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" onClick={() => handleAccept(invId)}>{t('common.accept')}</Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDecline(invId)}>{t('common.decline')}</Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {pendingApplicationCount > 0 && (
            <section className="space-y-2 pt-3">
              <h2 className="text-sm font-semibold text-foreground">{t('team.directory.reviewRequests')}</h2>
              <p className="text-sm text-muted-foreground">{t('team.directory.reviewRequestsHint')}</p>
              <ul className="divide-y divide-border rounded-lg border border-border bg-card">
                {myTeams.filter((team) => (pendingApplications.get(getEntityId(team)) ?? 0) > 0).map((team) => (
                  <li key={getEntityId(team)}>
                    <Link to={'/teams/' + getEntityId(team) + '/applications'} className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-accent/30">
                      <span className="font-medium text-foreground">{team.name}</span>
                      <span className="text-primary">{(pendingApplications.get(getEntityId(team)) ?? 0) > 0 ? t('team.directory.requestsCount', { count: pendingApplications.get(getEntityId(team)) ?? 0 }) : t('team.directory.openRequests')}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <TeamCreateDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          void loadMine();
          if (id) navigate(`/teams/${id}`);
        }}
      />

      {joinTarget && <JoinRequestDialog teamId={getEntityId(joinTarget)} teamName={joinTarget.name}
        onClose={() => setJoinTarget(null)} onSuccess={() => {
          setRequestedIds((current) => new Set(current).add(getEntityId(joinTarget)));
          setJoinTarget(null);
        }} />}

      <AnchoredMenu
        anchor={contextAnchor}
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        ariaLabel={t('team.card.actions')}
        className="min-w-52 py-1"
      >
        <MenuItem label={t('common.open')} onSelect={() => openTeamFromMenu('current')} />
        <MenuItem label={t('team.card.openInTab')} onSelect={() => openTeamFromMenu('tab')} />
        {contextMenu && myTeams.some((team) => getEntityId(team) === getEntityId(contextMenu.team)) && (
          <MenuItem label={pinnedIds.includes(getEntityId(contextMenu.team)) ? t('team.directory.unpin') : t('team.directory.pin')}
            onSelect={() => togglePin(getEntityId(contextMenu.team))} />
        )}
        {!narrow && (
          <>
            <MenuSeparator />
            <MenuItem label={t('team.card.openSplitLeft')} onSelect={() => openTeamFromMenu('left')} />
            <MenuItem label={t('team.card.openSplitRight')} onSelect={() => openTeamFromMenu('right')} />
            <MenuItem label={t('team.card.openSplitUp')} onSelect={() => openTeamFromMenu('up')} />
            <MenuItem label={t('team.card.openSplitDown')} onSelect={() => openTeamFromMenu('down')} />
          </>
        )}
      </AnchoredMenu>
    </div>
  );
});

type TeamSurfaceProps = {
  team: TeamCardData;
  parentName?: string;
  role: string;
  pinned: boolean;
  requested: boolean;
  onOpen: () => void;
  onJoin?: () => void;
  onContextMenu: (e: MouseEvent<HTMLElement>) => void;
  onMenuClick: (rect: DOMRect) => void;
};

function TeamGridCard({ team, parentName, role, pinned, requested, onOpen, onJoin, onContextMenu, onMenuClick }: TeamSurfaceProps) {
  const { t } = useI18n();
  const id = getEntityId(team);
  const status = getStatusMapping(teamStatusMap, team.status ?? '');
  const typeInfo = teamTypeMap[team.team_type ?? ''];
  const memberCount = team.members_count ?? team.member_count;

  return (
    <li className="min-w-0" onContextMenu={onContextMenu}>
      <Card variant="outlined" padding="none" className="flex h-full flex-col !rounded-lg border border-border !shadow-none transition-colors hover:border-primary/40 hover:bg-accent/20">
        <div className="flex items-start gap-2 px-4 pt-4">
          <Link to={'/teams/' + id} onClick={onOpen}
            className="flex min-w-0 flex-1 items-start gap-3 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('team.card.openTeam', { name: team.name || t('common.untitled') })}>
            <EntityAvatar name={team.name} resource={team.avatar} shape="square" size="md" />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-1.5">
                <span className="truncate text-sm font-semibold text-foreground">{team.name || t('common.untitled')}</span>
                {pinned && <span className="text-primary" aria-label={t('team.directory.pinned')}>★</span>}
              </span>
              {typeInfo && <span className="mt-0.5 block text-xs text-muted-foreground">{t(typeInfo.labelKey)}</span>}
            </span>
          </Link>
          <button type="button" onClick={(event) => onMenuClick(event.currentTarget.getBoundingClientRect())}
            aria-label={t('team.card.actionsFor', { name: team.name || t('common.untitled') })}
            aria-haspopup="menu"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <span aria-hidden="true">⋯</span>
          </button>
        </div>
        <div className="flex-1 px-4 pb-4 pt-3">
          {team.description && <p className="line-clamp-2 text-sm text-muted-foreground">{team.description}</p>}
          {parentName && <p className="mt-2 truncate text-xs text-muted-foreground">{parentName}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-3">
          <Badge variant={status.variant as never} size="sm">{t(status.labelKey)}</Badge>
          <span className="text-xs text-muted-foreground">{role}</span>
          {memberCount !== undefined && <span className="ml-auto text-xs tabular-nums text-muted-foreground">{t('team.memberCount', { count: memberCount })}</span>}
          {onJoin && <Button size="sm" variant="secondary" disabled={requested} onClick={onJoin} className="ml-auto">
            {t(requested ? 'team.directory.requested' : 'team.directory.requestJoin')}
          </Button>}
        </div>
      </Card>
    </li>
  );
}

function TeamCard({
  team,
  depth,
  childCount,
  collapsed,
  onToggle,
  parentName,
  role,
  pinned,
  requested,
  onOpen,
  onJoin,
  onContextMenu,
  onMenuClick,
}: {
  team: TeamCardData;
  depth: number;
  childCount: number;
  collapsed: boolean;
  onToggle: () => void;
  parentName?: string;
  role: string;
  pinned: boolean;
  requested: boolean;
  onOpen: () => void;
  onJoin?: () => void;
  onContextMenu: (e: MouseEvent<HTMLElement>) => void;
  onMenuClick: (rect: DOMRect) => void;
}) {
  const { t } = useI18n();
  const id = getEntityId(team);
  const status = getStatusMapping(teamStatusMap, team.status ?? '');
  const typeInfo = teamTypeMap[team.team_type ?? ''];

  return (
    <li className="flex min-w-0 items-center gap-2 px-3 py-3 hover:bg-accent/20" onContextMenu={onContextMenu}>
      <div className="flex min-w-0 flex-1 items-center gap-2" style={{ paddingLeft: depth * 20 }}>
        {childCount > 0 && (
          <button type="button" aria-label={t(collapsed ? 'team.directory.expand' : 'team.directory.collapse')}
            aria-expanded={!collapsed} onClick={onToggle}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent">
            <span aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
          </button>
        )}
        <Link to={'/teams/' + id} onClick={onOpen}
          className="flex min-w-0 flex-1 items-center gap-3 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t('team.card.openTeam', { name: team.name || t('common.untitled') })}>
          <EntityAvatar name={team.name} resource={team.avatar} shape="square" size="sm" />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              <span className="truncate text-sm font-semibold text-foreground">{team.name || t('common.untitled')}</span>
              {pinned && <span className="text-primary" aria-label={t('team.directory.pinned')}>★</span>}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {parentName ? parentName + ' · ' : ''}{team.description || (typeInfo ? t(typeInfo.labelKey) : '')}
            </span>
          </span>
        </Link>
      </div>
      <div className="hidden shrink-0 items-center gap-2 sm:flex">
        <Badge variant={status.variant as never} size="sm">{t(status.labelKey)}</Badge>
        <span className="w-24 truncate text-xs text-muted-foreground">{role}</span>
      </div>
      {onJoin && <Button size="sm" variant="secondary" disabled={requested} onClick={onJoin}>
        {t(requested ? 'team.directory.requested' : 'team.directory.requestJoin')}
      </Button>}
      <button type="button" onClick={(event) => onMenuClick(event.currentTarget.getBoundingClientRect())}
        aria-label={t('team.card.actionsFor', { name: team.name || t('common.untitled') })}
        aria-haspopup="menu"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <span aria-hidden="true">⋯</span>
      </button>
    </li>
  );
}

export default TeamListPage;
